import { define, html, router, store } from "hybrids";

import layout from "~/factories/layout";

import ArtistVideo from "~/stores/ArtistVideo";
import { Patron } from "~/stores/Broadcast";
import Session from "~/stores/Session";
import User from "~/stores/User";
import UserDetails from "~/stores/UserDetails";
import UserFollow from "~/stores/UserFollow";

export interface VVideoUserView {
  session: Session;
  user?: User;
  video?: ArtistVideo;
  featuredVideos: ArtistVideo[];
  details: UserDetails;
  follow: UserFollow;
  header: string;
  patron?: Patron;
  member?: boolean;
}

function toggleFollow(host: VVideoUserView) {
  if (store.ready(host.follow)) {
    store.set(host.follow, null);
  } else {
    store.set(UserFollow, { user: host.user });
  }
}

export default define<VVideoUserView>({
  [router.connect]: { replace: true },
  tag: "v-video-user-view",
  session: store(Session),
  user: store(User),
  video: store(ArtistVideo),
  featuredVideos: store([ArtistVideo], {
    id: ({ user }) => ({
      featured: true,
      artist_id: user?.id ?? "",
      artist_type: user?.type ?? "",
      page: 1,
    }),
  }),
  details: store(UserDetails, { id: ({ user }) => user?.id }),
  follow: store(UserFollow, { id: ({ user }) => user?.id }),
  header: ({ video, user }) => {
    if (!user || !video || !store.ready(video)) return "";

    if (video.collaborators.includes(user)) {
      return "Artists";
    }

    if (video.sponsors.find((s) => s.profile === user)) {
      return "Sponsors";
    }

    return "People";
  },
  patron: ({ video, user }) =>
    video && store.ready(video)
      ? video.patrons.find((p) => p.user === user)
      : undefined,
  member: ({ user }) => user?.member,
  render: layout("flex"),
  content: ({
    session,
    video,
    user,
    patron,
    member,
    featuredVideos,
    details,
    follow,
    header,
  }) => html`
    <v-broadcast-container overflow>
      <v-broadcast-nav-bar back>${header}</v-broadcast-nav-bar>
      ${video &&
      user &&
      store.ready(video, user) &&
      html`
        <v-ui-flexbox flow="row" layout="start center">
          <v-ui-flexbox grow="2">
            <v-ui-text>
              <v-broadcast-user
                user="${user}"
                broadcast="${video}"
                large
                status="${false}"
              ></v-broadcast-user>
            </v-ui-text>
          </v-ui-flexbox>
          ${store.ready(session) &&
          user !== session.user &&
          (store.ready(follow) || store.error(follow)) &&
          html`<v-ui-flexbox grow="1" layout="stretch">
            <v-ui-button-group>
              <v-ui-button
                name="${store.ready(follow) ? "Following" : "Follow"}"
                onclick="${toggleFollow}"
              ></v-ui-button>
            </v-ui-button-group>
          </v-ui-flexbox>`}
        </v-ui-flexbox>

        <v-ui-flexbox gap="medium">
          <section>
            <v-ui-text
              variant="h2"
              weight="semibold"
              color="${member ? "member" : patron ? "patron" : ""}"
              ygutter="1"
              selectable
            >
              ${user.registered &&
              html`<v-ui-link href=${user.pathname} target="_blank" rel="noreferer noopener">
                ${user.name}
              </v-ui-link> `}
              ${!user.registered && user.name}
              ${patron &&
              html`<v-ui-icon
                name="donate"
                color="${member ? "member" : "patron"}"
              ></v-ui-icon>`}
            </v-ui-text>
            <v-ui-text color="text-secondary" ygutter="1" selectable>
              @${user.username}
            </v-ui-text>
          </section>
          ${member &&
          html`<v-ui-text color="member">
            ${user.name} is a Viewcy member.
            <v-ui-link href="/pages/memberships" target="_blank" rel="noreferer noopener" underline>
              Become a member now
            </v-ui-link>
            to support and preserve our global heritage.
          </v-ui-text>`}
        </v-ui-flexbox>

        ${store.ready(details) &&
        details.fullDescription &&
        html`
          <v-broadcast-divider></v-broadcast-divider>
          <v-broadcast-people-read-more>
            ${details.fullDescription
              .trim()
              .substring(0, 125)
              .split("\n")
              .map((line) => html`<v-ui-text>${line}</v-ui-text>`)}
            ${details.fullDescription.length > 125 &&
            details.fullDescription
              .split("\n")
              .map((line) => html`<v-ui-text slot="more">${line}</v-ui-text>`)}
          </v-broadcast-people-read-more>
        `}
        ${store.ready(featuredVideos) &&
        !!featuredVideos.length &&
        html`
          <v-broadcast-divider></v-broadcast-divider>
          <v-ui-flexbox gap="small">
            <v-broadcast-people-header
              >Featured Videos</v-broadcast-people-header
            >
            <v-ui-flexbox gap="medium">
              ${featuredVideos.map(
                (video) => html`<v-video-card video="${video}"></v-video-card>`
              )}
            </v-ui-flexbox>
          </v-ui-flexbox>
        `}
      `}
    </v-broadcast-container>
  `,
});
